import React, { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Pencil, Trash2, X, TrendingUp,
  Wallet, Sparkles, Receipt, Percent, CalendarDays, Zap, Building2, Landmark, Download,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EXPENSE_CATEGORIES = [
  { value: "utenze", label: "Utenze" },
  { value: "pulizie", label: "Prodotti pulizie" },
  { value: "altro", label: "Altro" },
];
const catLabel = (v) => (EXPENSE_CATEGORIES.find((c) => c.value === v) || EXPENSE_CATEGORIES[2]).label;

const CHANNELS = [
  { value: "sito", label: "Sito", color: "#0F292F" },
  { value: "airbnb", label: "Airbnb", color: "#FF5A5F" },
  { value: "booking", label: "Booking", color: "#003580" },
  { value: "other", label: "Altro", color: "#D4AF37" },
];
const chMeta = (v) => CHANNELS.find((c) => c.value === v) || CHANNELS[3];
const eur = (n) => `€${Math.round(Number(n || 0)).toLocaleString("it-IT")}`;
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MONTHS_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const EMPTY = {
  apartment_id: "", channel: "sito", guest_name: "", arrival_date: "", departure_date: "",
  gross_amount: 0, platform_fee: 0, cleaning_cost: 0, tax: 0, other_costs: 0, tourist_tax: 0, notes: "",
};

const net = (r) =>
  Number(r.gross_amount || 0) - Number(r.platform_fee || 0) - Number(r.cleaning_cost || 0) - Number(r.tax || 0) - Number(r.other_costs || 0);

function Kpi({ id, icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-card border border-line rounded-sm p-5" data-testid={`kpi-${id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline text-[10px] text-muted-foreground">{label}</span>
        <Icon size={18} strokeWidth={1.5} style={{ color: accent || "#D4AF37" }} />
      </div>
      <div className="font-serif text-3xl text-petrol">{value}</div>
      {sub}
    </div>
  );
}

function TrendsChart({ months, selectedMonth, onSelect }) {
  const W = 760, H = 190, pad = 26, top = 12;
  const colW = (W - pad * 2) / 12;
  const bw = colW * 0.56;
  const usableH = H - top;
  const max = Math.max(1, ...months.map((m) => Math.max(m.net, 0)));
  return (
    <svg viewBox={`0 0 ${W} ${H + 26}`} className="w-full" data-testid="trends-chart">
      {[0, 0.5, 1].map((f) => {
        const yv = top + usableH - f * usableH;
        return (
          <g key={f}>
            <line x1={pad} x2={W - pad} y1={yv} y2={yv} stroke="#E2DCD0" strokeWidth="1" strokeDasharray={f === 0 ? "0" : "3 4"} />
            <text x={4} y={yv + 3} fontSize="9" fill="#9a9a9a">{Math.round((max * f))}</text>
          </g>
        );
      })}
      {months.map((mb, i) => {
        const x = pad + i * colW + (colW - bw) / 2;
        let yBottom = top + usableH;
        const isSel = selectedMonth === i + 1;
        return (
          <g key={i} onClick={() => onSelect(i + 1)} style={{ cursor: "pointer" }}>
            <rect x={pad + i * colW} y={top} width={colW} height={usableH} fill={isSel ? "rgba(212,175,55,0.10)" : "transparent"} />
            {CHANNELS.map((c) => {
              const v = Math.max(mb.channels?.[c.value] || 0, 0);
              if (v <= 0) return null;
              const h = (v / max) * usableH;
              yBottom -= h;
              return <rect key={c.value} x={x} y={yBottom} width={bw} height={h} fill={c.color} rx="1">
                <title>{`${MONTHS_SHORT[i]} · ${c.label}: ${eur(v)}`}</title>
              </rect>;
            })}
            <text x={x + bw / 2} y={H + 16} fontSize="10" textAnchor="middle" fill={isSel ? "#0F292F" : "#9a9a9a"} fontWeight={isSel ? 600 : 400}>{MONTHS_SHORT[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function AdminAnalytics() {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [apartments, setApartments] = useState([]);
  const [aptFilter, setAptFilter] = useState("");
  const [reservations, setReservations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expenseApt, setExpenseApt] = useState(null); // apartment object when managing spese
  const [newExpense, setNewExpense] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);

  const [y, m] = month.split("-").map(Number);

  const load = useCallback(() => {
    const params = { month };
    if (aptFilter) params.apartment_id = aptFilter;
    api.get("/admin/reservations", { params }).then((r) => setReservations(r.data)).catch(() => {});
    const sp = { date_from: `${month}-01`, date_to: `${month}-31` };
    if (aptFilter) sp.apartment_id = aptFilter;
    api.get("/admin/analytics/summary", { params: sp }).then((r) => setSummary(r.data)).catch(() => {});
    const tp = { year: month.split("-")[0] };
    if (aptFilter) tp.apartment_id = aptFilter;
    api.get("/admin/analytics/trends", { params: tp }).then((r) => setTrends(r.data)).catch(() => {});
    const ep = { month };
    if (aptFilter) ep.apartment_id = aptFilter;
    api.get("/admin/expenses", { params: ep }).then((r) => setExpenses(r.data)).catch(() => {});
  }, [month, aptFilter]);

  useEffect(() => { api.get("/admin/apartments").then((r) => setApartments(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const calendar = useMemo(() => {
    const startWeekday = (new Date(y, m - 1, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${month}-${String(d).padStart(2, "0")}`;
      const res = reservations.filter((r) => r.arrival_date <= ds && ds < r.departure_date);
      cells.push({ day: d, ds, res });
    }
    return cells;
  }, [reservations, month, y, m]);

  const save = async () => {
    if (!editing.apartment_id || !editing.arrival_date || !editing.departure_date) {
      toast.error("Compila appartamento e date");
      return;
    }
    setSaving(true);
    const payload = {
      ...editing,
      gross_amount: Number(editing.gross_amount),
      platform_fee: Number(editing.platform_fee),
      cleaning_cost: Number(editing.cleaning_cost),
      tax: Number(editing.tax),
      other_costs: Number(editing.other_costs),
      tourist_tax: Number(editing.tourist_tax),
    };
    try {
      if (editing.id) await api.put(`/admin/reservations/${editing.id}`, payload);
      else await api.post("/admin/reservations", payload);
      toast.success("Prenotazione salvata");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa prenotazione?")) return;
    await api.delete(`/admin/reservations/${id}`);
    toast.success("Eliminata");
    load();
  };

  const set = (k, v) => setEditing({ ...editing, [k]: v });
  const t = summary?.totals;

  // Expense management
  const openExpenses = (apt) => {
    setExpenseApt(apt);
    setNewExpense({ category: "utenze", description: "", amount: "", date: `${month}-01` });
  };
  const expensesForApt = useMemo(
    () => (expenseApt ? expenses.filter((e) => e.apartment_id === expenseApt.id) : []),
    [expenses, expenseApt]
  );
  const addExpense = async () => {
    if (!newExpense.amount || Number(newExpense.amount) <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    setSavingExpense(true);
    try {
      await api.post("/admin/expenses", {
        apartment_id: expenseApt.id,
        category: newExpense.category,
        description: newExpense.description,
        amount: Number(newExpense.amount),
        date: newExpense.date || `${month}-01`,
      });
      toast.success("Spesa aggiunta");
      setNewExpense({ category: "utenze", description: "", amount: "", date: `${month}-01` });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingExpense(false);
    }
  };
  const removeExpense = async (id) => {
    await api.delete(`/admin/expenses/${id}`);
    toast.success("Spesa eliminata");
    load();
  };

  const exportTouristTax = async () => {
    try {
      const r = await api.get("/admin/analytics/tourist-tax-export", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `tassa_soggiorno_${month}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore esportazione");
    }
  };

  const exportAccounting = async () => {
    try {
      const r = await api.get("/admin/analytics/accounting-export", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `contabilita_${month}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore esportazione");
    }
  };

  // Month-over-month delta on net income
  const momDelta = (() => {
    if (!trends?.months) return null;
    const cur = trends.months[m - 1]?.net ?? null;
    const prev = m > 1 ? trends.months[m - 2]?.net : null;
    if (cur == null || prev == null) return null;
    return cur - prev;
  })();
  const bestCh = trends?.best_channel?.channel ? chMeta(trends.best_channel.channel) : null;

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif font-light text-4xl text-petrol">Analytics &amp; ROI</h1>
          <p className="text-muted-foreground">Prenotazioni per canale e redditività netta</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={aptFilter} onChange={(e) => setAptFilter(e.target.value)} data-testid="analytics-apt-filter"
            className="border border-line rounded-sm px-3 py-2.5 text-sm text-petrol bg-card outline-none">
            <option value="">Tutti gli appartamenti</option>
            {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={exportAccounting} data-testid="export-accounting" className="flex items-center gap-2 border border-petrol text-petrol px-4 py-2.5 rounded-sm hover:bg-beige transition-colors text-sm">
            <Download size={16} /> Contabilità CSV
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-reservation" className="flex items-center gap-2 bg-petrol text-white px-4 py-2.5 rounded-sm hover:bg-[#1A3A41] transition-colors text-sm">
            <Plus size={16} /> Prenotazione
          </button>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => shiftMonth(-1)} data-testid="prev-month" className="w-9 h-9 border border-line rounded-sm flex items-center justify-center text-petrol hover:bg-beige"><ChevronLeft size={18} /></button>
        <span className="font-serif text-2xl text-petrol w-52 text-center" data-testid="current-month">{MONTHS[m - 1]} {y}</span>
        <button onClick={() => shiftMonth(1)} data-testid="next-month" className="w-9 h-9 border border-line rounded-sm flex items-center justify-center text-petrol hover:bg-beige"><ChevronRight size={18} /></button>
      </div>

      {/* KPI */}
      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-8">
          <Kpi id="gross" icon={Wallet} label="Incasso lordo" value={eur(t.gross)} accent="#0F292F" />
          <Kpi id="fees" icon={Receipt} label="Commissioni" value={eur(t.platform_fee)} accent="#FF5A5F" />
          <Kpi id="cleaning" icon={Sparkles} label="Pulizie" value={eur(t.cleaning)} accent="#0EA5A0" />
          <Kpi id="tax" icon={Percent} label="Tasse" value={eur(t.tax)} accent="#9333EA" />
          <Kpi id="expenses" icon={Zap} label="Spese varie" value={eur(t.expenses)} accent="#EA580C" />
          <Kpi id="net" icon={TrendingUp} label="Incasso netto" value={eur(t.net)} accent="#16A34A"
            sub={momDelta != null && (
              <span className={`text-xs mt-1 inline-flex items-center gap-1 ${momDelta >= 0 ? "text-green-700" : "text-destructive"}`} data-testid="mom-delta">
                {momDelta >= 0 ? "▲" : "▼"} {eur(Math.abs(momDelta))} vs mese prec.
              </span>
            )} />
          <Kpi id="margin" icon={Percent} label="Margine netto" value={`${t.margin_pct || 0}%`} accent="#D4AF37" />
        </div>
      )}

      {/* Tassa di soggiorno (incassata per il Comune, separata dal netto) */}
      {t && (
        <div className="bg-petrol/5 border border-petrol/15 rounded-sm p-5 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="tourist-tax-banner">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm bg-petrol/10 flex items-center justify-center text-petrol shrink-0"><Landmark size={18} strokeWidth={1.5} /></span>
            <div>
              <div className="font-medium text-petrol">Tassa di soggiorno incassata</div>
              <div className="text-xs text-muted-foreground">Importo riscosso dagli ospiti da versare al Comune — non incluso nel netto</div>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:justify-end">
            <span className="font-serif text-3xl text-petrol" data-testid="tourist-tax-total">{eur(t.tourist_tax)}</span>
            <button onClick={exportTouristTax} data-testid="export-tourist-tax"
              className="flex items-center gap-2 border border-petrol text-petrol px-4 py-2.5 rounded-sm hover:bg-beige transition-colors text-sm whitespace-nowrap">
              <Download size={16} /> Esporta CSV
            </button>
          </div>
        </div>
      )}

      {/* Channel breakdown */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {CHANNELS.map((c) => {
            const b = summary.by_channel[c.value] || { count: 0, net: 0, gross: 0 };
            return (
              <div key={c.value} className="bg-card border border-line rounded-sm p-5" data-testid={`channel-${c.value}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full" style={{ background: c.color }} />
                  <span className="font-medium text-petrol">{c.label}</span>
                </div>
                <div className="text-2xl font-serif text-petrol">{b.count} <span className="text-sm text-muted-foreground">pren.</span></div>
                <div className="text-sm text-muted-foreground mt-1">Netto {eur(b.net)} · Lordo {eur(b.gross)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-apartment profitability + custom expenses */}
      {summary && summary.by_apartment && summary.by_apartment.length > 0 && (
        <div className="bg-card border border-line rounded-sm overflow-x-auto mb-10" data-testid="by-apartment-section">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-line text-petrol">
            <Building2 size={18} strokeWidth={1.5} className="text-gold" />
            <span className="font-serif text-xl">Redditività per appartamento</span>
            <span className="text-xs text-muted-foreground ml-2">{MONTHS[m - 1]} {y}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-beige text-petrol/70">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Appartamento</th>
                <th className="text-right px-4 py-3 font-medium">Pren.</th>
                <th className="text-right px-4 py-3 font-medium">Lordo</th>
                <th className="text-right px-4 py-3 font-medium">Spese varie</th>
                <th className="text-right px-4 py-3 font-medium">Netto</th>
                <th className="text-right px-4 py-3 font-medium">Tassa sogg.</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {summary.by_apartment.map((a) => {
                const apt = apartments.find((x) => x.id === a.apartment_id);
                return (
                  <tr key={a.apartment_id || a.apartment} className="border-t border-line" data-testid={`apt-roi-${a.apartment_id}`}>
                    <td className="px-4 py-3 text-petrol font-medium">{a.apartment}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{a.count}</td>
                    <td className="px-4 py-3 text-right text-petrol">{eur(a.gross)}</td>
                    <td className="px-4 py-3 text-right text-orange-600" data-testid={`apt-expenses-${a.apartment_id}`}>{eur(a.expenses)}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{eur(a.net)}</td>
                    <td className="px-4 py-3 text-right text-petrol/70" data-testid={`apt-tourist-tax-${a.apartment_id}`}>{eur(a.tourist_tax)}</td>
                    <td className="px-4 py-3 text-right">
                      {apt && (
                        <button onClick={() => openExpenses(apt)} data-testid={`manage-expenses-${a.apartment_id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-line rounded-sm px-3 py-1.5 text-petrol hover:bg-beige transition-colors">
                          <Zap size={13} /> Spese varie
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {trends && (
        <div className="grid lg:grid-cols-3 gap-4 mb-10">
          <div className="lg:col-span-2 bg-card border border-line rounded-sm p-5" data-testid="trends-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-petrol">
                <TrendingUp size={18} strokeWidth={1.5} className="text-gold" />
                <span className="font-serif text-xl">Andamento netto {trends.year}</span>
              </div>
              <span className="text-sm text-muted-foreground">Netto anno: <strong className="text-petrol">{eur(trends.year_net)}</strong></span>
            </div>
            <TrendsChart months={trends.months} selectedMonth={m} onSelect={(mm) => setMonth(`${y}-${String(mm).padStart(2, "0")}`)} />
            <div className="flex gap-4 mt-3 text-xs justify-center">
              {CHANNELS.map((c) => (
                <span key={c.value} className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.label}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-petrol text-white rounded-sm p-6 flex flex-col justify-center" data-testid="best-channel-card">
            <span className="overline text-[10px] text-white/60">Canale più redditizio {trends.year}</span>
            {bestCh ? (
              <>
                <div className="flex items-center gap-2 mt-3">
                  <span className="w-4 h-4 rounded-full" style={{ background: bestCh.color }} />
                  <span className="font-serif text-3xl">{bestCh.label}</span>
                </div>
                <div className="text-gold font-serif text-4xl mt-2">{eur(trends.best_channel.net)}</div>
                <span className="text-white/60 text-xs mt-1">netto annuo da questo canale</span>
              </>
            ) : (
              <span className="text-white/50 mt-4">Nessun dato per quest&rsquo;anno.</span>
            )}
            <div className="mt-5 pt-5 border-t border-white/10 space-y-2">
              {CHANNELS.map((c) => (
                <div key={c.value} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-white/80"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.label}</span>
                  <span className="text-white/90">{eur(trends.by_channel_year?.[c.value] || 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="bg-card border border-line rounded-sm p-5 mb-10">
        <div className="flex items-center gap-2 mb-4 text-petrol">
          <CalendarDays size={18} strokeWidth={1.5} className="text-gold" />
          <span className="font-serif text-xl">Calendario prenotazioni</span>
          <div className="flex gap-3 ml-auto text-xs">
            {CHANNELS.map((c) => (
              <span key={c.value} className="flex items-center gap-1.5 text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.label}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {WEEKDAYS.map((w) => <div key={w} className="text-center overline text-[10px] text-muted-foreground py-2">{w}</div>)}
          {calendar.map((cell, i) => (
            <div key={i} className={`min-h-[88px] rounded-sm border p-1.5 ${cell ? "border-line bg-cream" : "border-transparent"}`}>
              {cell && (
                <>
                  <div className="text-xs text-muted-foreground mb-1">{cell.day}</div>
                  <div className="space-y-1">
                    {cell.res.slice(0, 3).map((r) => (
                      <button
                        key={r.id}
                        onClick={() => setEditing({ ...EMPTY, ...r })}
                        data-testid={`cal-res-${r.id}`}
                        className="w-full text-left rounded-sm px-1.5 py-0.5 text-[10px] text-white truncate"
                        style={{ background: chMeta(r.channel).color }}
                        title={`${chMeta(r.channel).label} · ${r.guest_name || ""} · ${eur(net(r))}`}
                      >
                        {r.guest_name || chMeta(r.channel).label}
                      </button>
                    ))}
                    {cell.res.length > 3 && <span className="text-[10px] text-muted-foreground">+{cell.res.length - 3}</span>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Reservations table */}
      <div className="bg-card border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-beige text-petrol/70">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Canale</th>
              <th className="text-left px-4 py-3 font-medium">Ospite</th>
              <th className="text-left px-4 py-3 font-medium">Appartamento</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-right px-4 py-3 font-medium">Lordo</th>
              <th className="text-right px-4 py-3 font-medium">Commis.</th>
              <th className="text-right px-4 py-3 font-medium">Pulizie</th>
              <th className="text-right px-4 py-3 font-medium">Tasse</th>
              <th className="text-right px-4 py-3 font-medium">Netto</th>
              <th className="text-right px-4 py-3 font-medium">Tassa sogg.</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {reservations.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">Nessuna prenotazione in questo mese.</td></tr>}
            {reservations.map((r) => (
              <tr key={r.id} className="border-t border-line" data-testid={`res-row-${r.id}`}>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: chMeta(r.channel).color }} />{chMeta(r.channel).label}</span></td>
                <td className="px-4 py-3 text-petrol">{r.guest_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.apartment_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.arrival_date} → {r.departure_date}</td>
                <td className="px-4 py-3 text-right text-petrol">{eur(r.gross_amount)}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">{eur(r.platform_fee)}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">{eur(r.cleaning_cost)}</td>
                <td className="px-4 py-3 text-right text-muted-foreground">{eur(r.tax)}</td>
                <td className="px-4 py-3 text-right font-medium text-green-700">{eur(r.net_amount ?? net(r))}</td>
                <td className="px-4 py-3 text-right text-petrol/70">{eur(r.tourist_tax)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditing({ ...EMPTY, ...r })} data-testid={`edit-res-${r.id}`} className="text-petrol hover:text-gold"><Pencil size={15} /></button>
                    <button onClick={() => remove(r.id)} data-testid={`del-res-${r.id}`} className="text-destructive hover:opacity-70"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setEditing(null)}>
          <div className="bg-cream rounded-sm w-full max-w-xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="reservation-editor">
            <div className="flex items-center justify-between p-5 border-b border-line">
              <h2 className="font-serif text-2xl text-petrol">{editing.id ? "Modifica prenotazione" : "Nuova prenotazione"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} className="text-muted-foreground" /></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Appartamento *</span>
                <select value={editing.apartment_id} onChange={(e) => set("apartment_id", e.target.value)} data-testid="res-apartment"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  <option value="">Seleziona...</option>
                  {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Canale *</span>
                <select value={editing.channel} onChange={(e) => set("channel", e.target.value)} data-testid="res-channel"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Ospite</span>
                <input value={editing.guest_name} onChange={(e) => set("guest_name", e.target.value)} data-testid="res-guest"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Arrivo *</span>
                <input type="date" value={editing.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} data-testid="res-arrival"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Partenza *</span>
                <input type="date" value={editing.departure_date} onChange={(e) => set("departure_date", e.target.value)} data-testid="res-departure"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Incasso lordo (€)</span>
                <input type="number" value={editing.gross_amount} onChange={(e) => set("gross_amount", e.target.value)} data-testid="res-gross"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Commissione piattaforma (€)</span>
                <input type="number" value={editing.platform_fee} onChange={(e) => set("platform_fee", e.target.value)} data-testid="res-fee"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Costo pulizie (€)</span>
                <input type="number" value={editing.cleaning_cost} onChange={(e) => set("cleaning_cost", e.target.value)} data-testid="res-cleaning"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Tasse (€)</span>
                <input type="number" value={editing.tax} onChange={(e) => set("tax", e.target.value)} data-testid="res-tax"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Altri costi (€)</span>
                <input type="number" value={editing.other_costs} onChange={(e) => set("other_costs", e.target.value)}
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Tassa di soggiorno (€)</span>
                <input type="number" value={editing.tourist_tax} onChange={(e) => set("tourist_tax", e.target.value)} data-testid="res-tourist-tax"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
                <span className="text-[11px] text-muted-foreground mt-1 block">Importo incassato dall'ospite per il Comune — tracciato a parte, NON sottratto dal netto. Personalizzabile per ogni prenotazione/periodo.</span>
              </label>
              <div className="sm:col-span-2 bg-petrol text-white rounded-sm px-4 py-3 flex items-center justify-between">
                <span className="overline text-[10px] text-white/70">Incasso netto stimato</span>
                <span className="font-serif text-2xl text-gold" data-testid="res-net">{eur(net(editing))}</span>
              </div>
            </div>
            <div className="p-5 border-t border-line flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-line text-petrol rounded-sm hover:bg-beige transition-colors">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="save-reservation" className="px-6 py-2.5 bg-petrol text-white rounded-sm hover:bg-[#1A3A41] transition-colors disabled:opacity-60">
                {saving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense manager modal */}
      {expenseApt && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setExpenseApt(null)}>
          <div className="bg-cream rounded-sm w-full max-w-lg my-auto" onClick={(e) => e.stopPropagation()} data-testid="expense-manager">
            <div className="flex items-center justify-between p-5 border-b border-line">
              <div>
                <h2 className="font-serif text-2xl text-petrol">Spese varie</h2>
                <p className="text-sm text-muted-foreground">{expenseApt.name} · {MONTHS[m - 1]} {y}</p>
              </div>
              <button onClick={() => setExpenseApt(null)}><X size={20} className="text-muted-foreground" /></button>
            </div>

            {/* Add form */}
            <div className="p-5 border-b border-line grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Categoria</span>
                <select value={newExpense.category} onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })} data-testid="expense-category"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Importo (€)</span>
                <input type="number" min="0" step="0.01" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} data-testid="expense-amount"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Data</span>
                <input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} data-testid="expense-date"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Descrizione</span>
                <input value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} data-testid="expense-description"
                  placeholder="es. Bolletta luce" className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <div className="col-span-2">
                <button onClick={addExpense} disabled={savingExpense} data-testid="add-expense"
                  className="w-full flex items-center justify-center gap-2 bg-petrol text-white px-4 py-2.5 rounded-sm hover:bg-[#1A3A41] transition-colors text-sm disabled:opacity-60">
                  <Plus size={16} /> {savingExpense ? "Aggiunta..." : "Aggiungi spesa"}
                </button>
              </div>
            </div>

            {/* List */}
            <div className="p-5 max-h-72 overflow-y-auto">
              {expensesForApt.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nessuna spesa registrata in questo mese.</p>
              ) : (
                <div className="space-y-2" data-testid="expense-list">
                  {expensesForApt.map((e) => (
                    <div key={e.id} className="flex items-center justify-between border border-line rounded-sm px-3 py-2" data-testid={`expense-row-${e.id}`}>
                      <div>
                        <div className="text-sm text-petrol font-medium">{catLabel(e.category)} <span className="text-muted-foreground font-normal">{e.description ? `· ${e.description}` : ""}</span></div>
                        <div className="text-xs text-muted-foreground">{e.date}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-orange-600">{eur(e.amount)}</span>
                        <button onClick={() => removeExpense(e.id)} data-testid={`del-expense-${e.id}`} className="text-destructive hover:opacity-70"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t border-line mt-2">
                    <span className="text-sm text-petrol font-medium">Totale spese mese</span>
                    <span className="text-sm font-serif text-orange-600" data-testid="expense-total">
                      {eur(expensesForApt.reduce((s, e) => s + Number(e.amount || 0), 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
